import { log } from "../util/log";
import ActionControl from "./ActionControl";
import { HOVER_DELAY } from "./constants";
import ContextMenu from "./ContextMenu";
import Icon from "./Icon";
import PortalMenu from "./PortalMenu";
import * as _ from "lodash";
import * as React from "react";
import { Button, Dropdown, MenuItem } from "react-bootstrap";
import * as ReactDOM from "react-dom";
function nop() {
    // nop
}
function genTooltip(show) {
    return typeof show === "string" ? show : undefined;
}
function MenuAction(props) {
    const { t, action, instanceId, onSelect, id } = props;
    const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
    const [open, setOpenMenu] = React.useState(false);
    const [subMenus, setSubMenus] = React.useState([]);
    const setOpen = React.useCallback((value) => {
        if (subMenus.length === 0) {
            if (Array.isArray(action.subMenus)) {
                setSubMenus(action.subMenus);
            }
            else {
                setSubMenus(action.subMenus());
            }
        }
        setOpenMenu(value);
    }, [setOpenMenu, action.subMenus]);
    const setOpenFalse = React.useCallback(() => {
        setOpen(false);
    }, [setOpen]);
    const itemRef = React.useRef();
    const hideTimer = React.useRef();
    const trigger = React.useCallback(() => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
        }
        action.action?.(instanceIds, action.data);
        if (action.subMenus !== undefined) {
            setOpen((old) => !old);
        }
        else {
            onSelect?.();
        }
    }, [instanceId, action, onSelect, setOpen]);
    const show = React.useCallback(() => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
        }
        hideTimer.current = setTimeout(() => {
            setOpen(true);
        }, HOVER_DELAY);
    }, [setOpen, hideTimer.current]);
    const hide = React.useCallback(() => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
        }
        hideTimer.current = setTimeout(() => {
            setOpen(false);
        }, HOVER_DELAY);
    }, [setOpen]);
    const setItemRef = (ref) => {
        itemRef.current = ReactDOM.findDOMNode(ref);
    };
    return (React.createElement(MenuItem, { eventKey: id, onSelect: trigger, onMouseEnter: action.subMenus !== undefined ? show : undefined, onMouseLeave: action.subMenus !== undefined ? hide : undefined, disabled: action.show !== true, ref: setItemRef, title: genTooltip(action.show) },
        React.createElement(Icon, { name: action.icon }),
        React.createElement("div", { className: "button-text" }, t(action.title)),
        action.subMenus !== undefined ? (React.createElement(React.Fragment, null,
            React.createElement(ContextMenu, { instanceId: instanceId[0], visible: open, anchor: itemRef.current, onHide: setOpenFalse, actions: subMenus }),
            React.createElement(Icon, { className: "menu-more-icon", name: "showhide-right" }))) : null));
}
/**
 * represents an extensible row of icons/buttons/actions
 * In the simplest form this is simply a bunch of buttons that will run
 * an action if clicked, but an icon can also be more dynamic (i.e. rendering
 * dynamic content or having multiple states)
 *
 * @class IconBar
 * @extends {ComponentEx<IProps, {}>}
 */
class DropdownMenu extends React.PureComponent {
    mRef;
    constructor(props) {
        super(props);
        this.state = { open: false };
    }
    render() {
        const { t, actions, id, className } = this.props;
        const classes = [];
        if (className) {
            classes.push(className);
        }
        const defaultIdx = actions.findIndex((act) => act.show === true);
        if (defaultIdx === -1) {
            return null;
        }
        const rest = actions.slice(0);
        rest.splice(defaultIdx, 1);
        const title = (React.createElement("div", { title: genTooltip(actions[defaultIdx].show), style: { width: "100%", height: "100%" } },
            React.createElement(Icon, { name: actions[defaultIdx].icon }),
            t(actions[defaultIdx].title)));
        const btn = (React.createElement(Button, { onClick: actions[defaultIdx].show ? this.triggerDefault : undefined, "data-value": actions[defaultIdx].title }, title));
        if (rest.length === 0) {
            return btn;
        }
        return (React.createElement(Dropdown, { id: `${id}-menu`, ref: this.setRef },
            btn,
            React.createElement(Dropdown.Toggle, { open: this.state.open, onClick: this.toggleOpen }),
            React.createElement(PortalMenu, { open: this.state.open, target: this.mRef, onClose: this.close, onClick: nop, bsRole: "menu" }, rest.map((iter, idx) => this.renderMenuItem(iter, idx)))));
    }
    setRef = (ref) => {
        this.mRef = ReactDOM.findDOMNode(ref);
    };
    close = () => {
        this.setState({ open: false });
    };
    toggleOpen = (evt) => {
        evt.preventDefault();
        this.setState({ open: !this.state.open });
    };
    renderMenuItem = (action, index) => {
        const { t, instanceId } = this.props;
        const id = `${instanceId || "1"}_${index}`;
        if (action.icon === null && action.component === undefined) {
            return (React.createElement(MenuItem, { className: "menu-separator-line", key: id, disabled: true }, t(action.title)));
        }
        if (action.icon !== undefined) {
            return (React.createElement(MenuAction, { t: t, key: id, id: id, action: action, instanceId: instanceId }));
        }
        else {
            return (React.createElement(MenuItem, { key: id, eventKey: id, disabled: action.show !== true, title: genTooltip(action.show) }, this.renderCustomIcon(id, action)));
        }
    };
    renderCustomIcon(id, action) {
        // custom case. the caller can pass properties via the props() function and by
        // passing the prop to the iconbar. the props on the iconbar that we don't handle are
        // passed on
        const knownProps = [
            "condition",
            "className",
            "group",
            "t",
            "i18nLoadedAt",
            "objects",
            "children",
        ];
        const unknownProps = Object.keys(this.props).reduce((prev, current) => {
            if (knownProps.indexOf(current) === -1) {
                return {
                    ...prev,
                    [current]: this.props[current],
                };
            }
            else {
                return prev;
            }
        }, {});
        const staticProps = {
            ...unknownProps,
            key: id,
            buttonType: this.props.buttonType,
            orientation: this.props.orientation,
        };
        if (action.props !== undefined) {
            const addProps = action.props();
            return (React.createElement(action.component, { ...staticProps, ...addProps, parentType: "dropdown" }));
        }
        else {
            return React.createElement(action.component, { ...staticProps, parentType: "dropdown" });
        }
    }
    triggerDefault = (evt) => {
        const { instanceId, actions } = this.props;
        const data = evt.currentTarget.attributes.getNamedItem("data-value");
        if (data === undefined) {
            log("error", "no default action", JSON.stringify(actions));
            return;
        }
        const title = data.value;
        const action = actions.find((iter) => iter.title === title);
        if (action !== undefined) {
            const instanceIds = typeof instanceId === "string" ? [instanceId] : instanceId;
            action.action?.(instanceIds);
        }
    };
}
class ActionDropdown extends React.Component {
    static ACTION_PROPS = [
        "filter",
        "group",
        "instanceId",
        "staticElements",
    ];
    render() {
        const actionProps = _.pick(this.props, ActionDropdown.ACTION_PROPS);
        const menuProps = _.omit(this.props, ActionDropdown.ACTION_PROPS);
        return (React.createElement(ActionControl, { ...actionProps },
            React.createElement(DropdownMenu, { ...menuProps })));
    }
}
export default ActionDropdown;
